import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Box, CssBaseline, ThemeProvider } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import AppSidebar from './components/layout/AppSidebar';
import { useAppTheme } from './theme';
import PageLoader from './components/common/PageLoader';
import PageTransition from './components/common/PageTransition';
import { AuthProvider } from './context/AuthContext';

// Lazy load pages
const pageLoaders = {
  Dashboard: () => import('./pages/Dashboard'),
  Settings: () => import('./pages/Settings'),
  Profile: () => import('./pages/Profile'),
  Login: () => import('./pages/Login'),
  PresetManagement: () => import('./pages/PresetManagement'),
};

const Dashboard = React.lazy(pageLoaders.Dashboard);
const Settings = React.lazy(pageLoaders.Settings);
const Profile = React.lazy(pageLoaders.Profile);
const Login = React.lazy(pageLoaders.Login);
const PresetManagement = React.lazy(pageLoaders.PresetManagement);

function AnimatedRoutes({ handleSidebarToggle, setTheme }) {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route 
          path="/" 
          element={
            <PageTransition>
              <Dashboard onMenuClick={handleSidebarToggle} />
            </PageTransition>
          } 
        />
        <Route 
          path="/profile" 
          element={
            <PageTransition>
              <Profile onMenuClick={handleSidebarToggle} />
            </PageTransition>
          } 
        />
        <Route 
          path="/settings" 
          element={
            <PageTransition>
              <Settings setTheme={setTheme} onMenuClick={handleSidebarToggle} />
            </PageTransition>
          } 
        />
        <Route 
          path="/login" 
          element={
            <PageTransition>
              <Login onMenuClick={handleSidebarToggle} />
            </PageTransition>
          } 
        />
        <Route 
          path="/presets" 
          element={
            <PageTransition>
              <PresetManagement onMenuClick={handleSidebarToggle} />
            </PageTransition>
          } 
        />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const drawerWidth = 240;
  const [theme, setTheme] = useAppTheme();

  useEffect(() => {
    // 页面加载完成后延迟执行预加载
    const timer = setTimeout(() => {
      Object.values(pageLoaders).forEach(loader => {
        loader().catch(err => console.debug('Preload skipped:', err));
      });
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleSidebarToggle = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppSidebar
            width={drawerWidth}
            isOpen={isSidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />
          <Box component="main" sx={{ flexGrow: 1, p: 2, overflow: 'hidden' }}>
            <React.Suspense fallback={<PageLoader />}>
              <AnimatedRoutes 
                handleSidebarToggle={handleSidebarToggle} 
                setTheme={setTheme} 
              />
            </React.Suspense>
          </Box>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
